import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import type { StudioApi } from '../engine/api';
import type { ConnectFragment, ShowDock } from '../net/connect-link';
import { createActions } from './actions';
import { UiContext, type UiContextValue } from './context';
import { App } from './shell/App';
import type { Theme } from './theme/theme';
import { createUiStore } from './uiStore';

/**
 * Renders the studio interface into `container` against the given kernel and
 * returns a function that unmounts it. The interface talks to the application
 * exclusively through `studio`. `connect` is a `#connect=` link read at load (main.tsx), applied once the
 * workspace is up; `theme`, `embedded` and `dock` come from `#theme=`, `#embed=1` and `#dock=` of the same fragment.
 */
export function mountStudioUi(
  container: HTMLElement,
  studio: StudioApi,
  options: { connect?: ConnectFragment; theme?: Theme | null; embedded?: boolean; dock?: ShowDock | null } = {},
): () => void {
  const ui = createUiStore({ theme: options.theme ?? null, embedded: options.embedded ?? false });
  const value: UiContextValue = { studio, ui, actions: createActions(studio, ui) };
  const root = createRoot(container);
  root.render(createElement(UiContext.Provider, { value }, createElement(App, { connect: options.connect, dock: options.dock })));

  let mounted = true;
  return () => {
    if (!mounted) return;
    mounted = false;
    root.unmount();
  };
}
